/**
 * Renders an in-page preview of the marketing email campaign.
 *
 * Previously this preview was built by string-interpolating merchant-supplied
 * values (store name, message, website, color, logo URL) into an HTML
 * template and feeding it to `dangerouslySetInnerHTML`. Even though the
 * merchant is "trusted" input, any field could break the markup or be
 * weaponized to embed scripts — and the dashboard renders multiple
 * merchants' previews in adjacent flows.
 *
 * This component reproduces the same visual layout with JSX so React escapes
 * text content automatically. URLs are sanitized to `http(s)://`-only before
 * rendering so `javascript:` and similar schemes can't sneak in via the
 * Visit / Footer links.
 */

import { LOYALPASS_EMAIL_BRAND_LINK_COLOR } from '../../../shared/constants/loyalPassBrand';
import {
    collectMarketingSocialLinks,
    marketingSocialIconImageUrl,
} from '../../../shared/utils/marketingEmailSocialLinks';

const IMAGE_EXTENSION_PATTERN = /\.(jpe?g|gif|png|webp|svg)$/i;

const isValidImage = (url) =>
    typeof url === 'string' && IMAGE_EXTENSION_PATTERN.test(url);

/**
 * Only allow http/https links. Anything else (including data:, javascript:,
 * file:, mailto:) returns null so the link is rendered as plain text.
 */
const sanitizeLink = (raw) => {
    if (!raw || typeof raw !== 'string') {
        return null;
    }
    const trimmed = raw.trim();
    if (!trimmed) {
        return null;
    }
    try {
        const url = new URL(
            trimmed,
            typeof window !== 'undefined' ? window.location.origin : 'https://example.com'
        );
        if (url.protocol === 'http:' || url.protocol === 'https:') {
            return url.toString();
        }
    } catch {
        // Fall through to null
    }
    return null;
};

export default function EmailCampaignPreview({
    storeName,
    storeColor,
    storeLogoUrl,
    storeWebsite,
    socialSettings = {},
    title,
    message,
}) {
    const safeWebsite = sanitizeLink(storeWebsite);
    const socialLinks = collectMarketingSocialLinks({
        website: storeWebsite,
        ...socialSettings,
    }).filter((link) => sanitizeLink(link.url));
    const showLogo = isValidImage(storeLogoUrl);

    const containerStyle = {
        maxWidth: 600,
        margin: 'auto',
        fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif",
        borderRadius: 14,
        overflow: 'hidden',
        backgroundColor: '#0D0221',
        border: '1px solid rgba(255,255,255,0.08)',
    };

    const headerStyle = {
        background: `linear-gradient(135deg, ${storeColor}, #FF4DA6)`,
        color: '#ffffff',
        padding: 24,
        textAlign: 'center',
    };

    const bodyStyle = {
        padding: 28,
        backgroundColor: '#14052e',
        color: '#EDE9FE',
    };

    return (
        <div className="border rounded overflow-hidden">
            <div style={containerStyle}>
                <div style={headerStyle}>
                    {showLogo && (
                        <img
                            src={storeLogoUrl}
                            alt={`${storeName} Logo`}
                            style={{
                                maxHeight: 80,
                                margin: '0 auto 12px',
                                display: 'block',
                            }}
                        />
                    )}
                    <h1 style={{ margin: 0, fontSize: 22, lineHeight: 1.2 }}>
                        {storeName}
                    </h1>
                    <h2
                        style={{
                            margin: '10px 0 0',
                            fontWeight: 600,
                            fontSize: 16,
                            opacity: 0.95,
                        }}
                    >
                        {title || 'Title Here'}
                    </h2>
                </div>

                <div style={bodyStyle}>
                    <p
                        style={{
                            lineHeight: 1.7,
                            fontSize: 16,
                            margin: 0,
                            color: '#EDE9FE',
                            textAlign: 'left',
                            whiteSpace: 'pre-wrap',
                        }}
                    >
                        {message || 'Your campaign message here'}
                    </p>

                    <p
                        style={{
                            margin: '16px 0 0',
                            fontSize: 14,
                            textAlign: 'center',
                            color: '#CBD5E1',
                        }}
                    >
                        We appreciate your loyalty!
                    </p>

                    {socialLinks.length > 0 && (
                        <div
                            style={{
                                margin: '22px 0 4px',
                                textAlign: 'center',
                                lineHeight: 0,
                            }}
                        >
                            {socialLinks.map((link) => {
                                const href = sanitizeLink(link.url);
                                if (!href) {
                                    return null;
                                }
                                return (
                                    <a
                                        key={`${link.icon}-${href}`}
                                        href={href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title={link.label}
                                        style={{
                                            display: 'inline-block',
                                            margin: '0 10px',
                                            textDecoration: 'none',
                                            verticalAlign: 'middle',
                                        }}
                                    >
                                        <img
                                            src={marketingSocialIconImageUrl(link.icon)}
                                            alt={link.label}
                                            width={28}
                                            height={28}
                                            style={{ display: 'block', border: 0 }}
                                        />
                                    </a>
                                );
                            })}
                        </div>
                    )}

                    <div
                        style={{
                            fontSize: 12,
                            color: '#9CA3AF',
                            textAlign: 'center',
                            margin: '26px 0 0',
                            paddingTop: 18,
                            borderTop: '1px solid rgba(255,255,255,0.10)',
                        }}
                    >
                        <p style={{ margin: 0 }}>
                            You’re receiving this email because you joined {storeName}’s loyalty program.
                            {safeWebsite && (
                                <>
                                    <br />
                                    Visit us at{' '}
                                    <a
                                        href={safeWebsite}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                            color: storeColor,
                                            textDecoration: 'none',
                                            fontWeight: 600,
                                        }}
                                    >
                                        {safeWebsite}
                                    </a>
                                </>
                            )}
                        </p>
                        <p style={{ margin: '16px 0 0', opacity: 0.7 }}>
                            Powered by{' '}
                            <a
                                href="https://www.loyalpassapp.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    fontSize: 12,
                                    color: LOYALPASS_EMAIL_BRAND_LINK_COLOR,
                                    textDecoration: 'underline',
                                    fontWeight: 600,
                                }}
                            >
                                LoyalPass
                            </a>
                        </p>
                        <p style={{ margin: '8px 0 0', opacity: 0.7, fontSize: 11 }}>
                            Support:{' '}
                            <a
                                href="mailto:loyalpassapp@gmail.com"
                                style={{ color: '#9CA3AF', textDecoration: 'none' }}
                            >
                                loyalpassapp@gmail.com
                            </a>
                        </p>
                        <p style={{ margin: '16px 0 0' }}>
                            <span
                                style={{
                                    color: '#9CA3AF',
                                    textDecoration: 'underline',
                                }}
                            >
                                Unsubscribe from marketing emails
                            </span>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
